import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { RotateCcw } from 'lucide-react';

import { usePendingOvertimeApi, useOvertimeDecisionsApi } from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import formatMinutes from '../../utils/formatMinutes';
import DataTable from '../../components/ui/DataTable';
import ReportExportButtons from '../../components/ui/ReportExportButtons';
import { buildReportLabels, statusReportLabels } from '../../utils/reportLabels';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';
import Tabs from '../../components/ui/Tabs';
import GenericFormDrawer from '../../components/GenericFormDrawer';

// AKM POC §5.1 — onayın ölçüsü HESAPLANAN fazla mesaidir: Bekleyenler'de onaylanabilir dakika
// (approvableMinutes = hesaplanan − önceden onaylı), Revize'de güncel onaylanabilir dakika
// (currentApprovableMinutes). Sunucu approvedMinutes'ı 1..onaylanabilir aralığında kabul eder.
export const approvableFor = (mode, row) => {
  if (!row) return 0;
  return (mode === 'approve' ? row.approvableMinutes : row.currentApprovableMinutes) ?? 0;
};

// Backend dakikayı int olarak alır — ondalık ("90.5") tutulmazsa 400 döner. Number.isInteger hem NaN'ı
// hem ondalığı eler.
export const isValidApprovedMinutes = (value, max) => {
  if (value === '' || value === null || value === undefined) return false;
  const num = Number(value);
  return Number.isInteger(num) && num >= 1 && num <= max;
};

const Container = styled.div``;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

// Onay / Revize / Ret formları GenericFormDrawer'dadır (UI altın kuralı: form = çekmece, modal DEĞİL).

const BulkFailNotice = styled.div`
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 6px;
  background: #fdecea;
  border: 1px solid #f5c6cb;
  font-family: 'Nunito Sans', sans-serif;
`;

const BulkFailTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #c0392b;
  margin-bottom: 4px;
`;

const BulkFailItem = styled.div`
  font-size: 12px;
  color: #c0392b;
  padding: 2px 0;
`;

export default function OvertimeApprovalsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [activeTab, setActiveTab] = useState('pending');

  const [pendingQueryParams, setPendingQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [pendingFilterList, setPendingFilterList] = useState([]);
  const [decidedQueryParams, setDecidedQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [decidedFilterList, setDecidedFilterList] = useState([]);

  const pending = usePendingOvertimeApi(pendingQueryParams);
  const decided = useOvertimeDecisionsApi(decidedQueryParams);

  // Onayla (Bekleyenler) / Revize Et (Kararlar) — aynı karar çekmecesi, mode ile.
  const [decisionDialog, setDecisionDialog] = useState({ isOpen: false, mode: null, row: null });
  const [rejectDialog, setRejectDialog] = useState({ isOpen: false, row: null });

  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkFailed, setBulkFailed] = useState([]);

  // ─── Bekleyenler: tablo durumu ───────────────────────────────────
  const handlePendingDataStateChange = useCallback((action, tableState) => {
    setPendingFilterList(tableState.filterList);
    setPendingQueryParams(parseTableParams(tableState, pending.columns));
  }, [pending.columns]);
  const handlePendingFilterConfirm = useCallback((newFilterList) => setPendingFilterList(newFilterList), []);

  // ─── Kararlar: tablo durumu ──────────────────────────────────────
  const handleDecidedDataStateChange = useCallback((action, tableState) => {
    setDecidedFilterList(tableState.filterList);
    setDecidedQueryParams(parseTableParams(tableState, decided.columns));
  }, [decided.columns]);
  const handleDecidedFilterConfirm = useCallback((newFilterList) => setDecidedFilterList(newFilterList), []);

  // ─── Onayla / Revize Et çekmecesi ─────────────────────────────────
  const handleApproveClick = useCallback((row) => {
    setDecisionDialog({ isOpen: true, mode: 'approve', row });
  }, []);

  const handleReviseOpen = useCallback((row) => {
    setDecisionDialog({ isOpen: true, mode: 'revise', row });
  }, []);

  const handleDecisionCancel = useCallback(() => {
    setDecisionDialog({ isOpen: false, mode: null, row: null });
  }, []);

  const minutesMax = approvableFor(decisionDialog.mode, decisionDialog.row);

  // Başlangıç değeri SABİT kimlikli (GenericFormDrawer formu [open, initialValues] değişince sıfırlar).
  // Onayla: varsayılan = onaylanabilir dakikanın tamamı. Revize: önceki onay hâlâ geçerli aralıktaysa
  // korunur; değilse (reddedilmişti ya da ölçüm küçüldü) güncel onaylanabilir dakikaya düşülür.
  const decisionInitialValues = useMemo(() => {
    const { mode, row } = decisionDialog;
    if (!row) return null;
    if (mode === 'approve') {
      return { approvedMinutes: minutesMax > 0 ? String(minutesMax) : '', notes: '' };
    }
    const keep = row.approvedMinutes > 0 && row.approvedMinutes <= minutesMax;
    const def = keep ? row.approvedMinutes : minutesMax;
    return { approvedMinutes: def > 0 ? String(def) : '', notes: row.notes || '' };
  }, [decisionDialog, minutesMax]);

  // Revize her zaman 'approved' POST'udur; reddedilmiş bir satır revize edilirse durum sessizce
  // reddedildi→onaylandı döner. Aksiyonu kısıtlamak yerine (yanlışlıkla reddedilen bir gün bir daha
  // düzeltilemezdi — karar verilmiş satırlar Bekleyenler'e dönmüyor) durum değişikliği açıkça uyarılır.
  const revisingRejected = decisionDialog.mode === 'revise' && decisionDialog.row?.status === 'rejected';

  const decisionSchema = useMemo(() => [
    {
      name: '_reviseRejected', type: 'notice', tone: 'warning',
      content: t('overtimeApproval.reviseRejectedWarning'),
      hidden: () => !revisingRejected,
    },
    {
      name: 'approvedMinutes', label: t('overtimeApproval.approvedMinutes'), type: 'number', grid: 12,
      required: true, min: 1, max: minutesMax, step: 1,
      description: `${t('overtimeApproval.approvableMinutes')}: ${minutesMax}`,
    },
    {
      // Aralık dışı değer alanın yanında söylenir (generic toast'a düşmesin); kayıt engellenir.
      name: '_minutesError', type: 'notice', tone: 'warning',
      content: t('overtimeApproval.approvedMinutesError', { max: minutesMax }),
      hidden: (fd) => isValidApprovedMinutes(fd.approvedMinutes, minutesMax),
    },
    { name: 'notes', label: t('overtimeApproval.notes'), type: 'textarea', grid: 12, rows: 3 },
  ], [t, minutesMax, revisingRejected]);

  // false dönerse çekmece AÇIK kalır (geçersiz dakika ya da sunucu reddi — toast interceptor'da).
  const handleDecisionSave = useCallback(async (formData) => {
    const { mode, row } = decisionDialog;
    if (!row || !isValidApprovedMinutes(formData?.approvedMinutes, minutesMax)) return false;
    const approvedMinutes = Number(formData.approvedMinutes);
    const notes = (formData.notes || '').trim() || undefined;
    const ok = mode === 'approve'
      ? await pending.decide({ userId: row.userId, date: row.date, status: 'approved', approvedMinutes, notes })
      : await decided.revise({ userId: row.userId, date: row.date, approvedMinutes, notes });
    if (!ok) return false;
    setDecisionDialog({ isOpen: false, mode: null, row: null });
    // Onayla: satır Bekleyenler'den düşer, Kararlar'a girer — o liste de tazelenir (görüntülenmiyor
    // olsa bile) ki sekme değiştirildiğinde bayat veri görünmesin.
    if (mode === 'approve') decided.refresh();
    return true;
  }, [decisionDialog, minutesMax, pending, decided]);

  // ─── Reddet çekmecesi ─────────────────────────────────────────────
  const handleRejectOpen = useCallback((row) => {
    setRejectDialog({ isOpen: true, row });
  }, []);

  const handleRejectCancel = useCallback(() => setRejectDialog({ isOpen: false, row: null }), []);

  const rejectInitialValues = useMemo(() => (rejectDialog.row ? { notes: '' } : null), [rejectDialog.row]);

  const rejectSchema = useMemo(() => [
    { name: 'notes', label: t('overtimeApproval.notes'), type: 'textarea', grid: 12, rows: 3, required: true },
  ], [t]);

  const handleRejectSave = useCallback(async (formData) => {
    const reason = (formData?.notes || '').trim();
    if (!reason || !rejectDialog.row) return false;
    const { row } = rejectDialog;
    const ok = await pending.decide({
      userId: row.userId, date: row.date, status: 'rejected', approvedMinutes: 0, notes: reason,
    });
    if (!ok) return false;
    setRejectDialog({ isOpen: false, row: null });
    decided.refresh();
    return true;
  }, [rejectDialog, pending, decided]);

  // ─── Toplu onay (görünen sayfa) ───────────────────────────────────
  // Her satır onaylanabilir dakikasının TAMAMIYLA onaylanır; onaylanabilir dakikası olmayan satır
  // gönderilmez (sunucu 1..onaylanabilir dışını invalidMinutes ile reddeder).
  const bulkRows = useMemo(() => pending.data.filter((r) => (r.approvableMinutes || 0) > 0), [pending.data]);
  const pendingTotalMinutes = useMemo(
    () => bulkRows.reduce((sum, r) => sum + (r.approvableMinutes || 0), 0),
    [bulkRows],
  );

  const handleBulkApproveConfirm = useCallback(async () => {
    const rows = bulkRows;
    setBulkProcessing(true);
    const items = rows.map((r) => ({
      userId: r.userId, date: r.date, status: 'approved', approvedMinutes: r.approvableMinutes,
    }));
    const result = await pending.decideBulk(items);
    setBulkProcessing(false);
    setBulkConfirmOpen(false);
    if (result) {
      const failedWithNames = (result.failed || []).map((f) => {
        const match = rows.find((r) => r.userId === f.userId && r.date === f.date);
        return { ...f, userFullName: match?.userFullName };
      });
      setBulkFailed(failedWithNames);
      // Onaylananlar Kararlar'a girer.
      decided.refresh();
    }
  }, [bulkRows, pending, decided]);

  // ─── Satır aksiyonları ────────────────────────────────────────────
  const pendingActionOptions = useMemo(() => {
    if (!canWrite('TA')) return [];
    return [
      { type: 'approve', label: t('overtimeApproval.approve'), onClick: handleApproveClick },
      { type: 'reject', label: t('overtimeApproval.reject'), onClick: handleRejectOpen },
    ];
  }, [canWrite, t, handleApproveClick, handleRejectOpen]);

  const decidedActionOptions = useMemo(() => {
    if (!canWrite('TA')) return [];
    return [
      { type: 'revise', label: t('overtimeApproval.revise'), icon: RotateCcw, color: '#F59E0B', onClick: handleReviseOpen },
    ];
  }, [canWrite, t, handleReviseOpen]);

  const pendingColumns = useMemo(() => {
    if (pendingActionOptions.length === 0) return pending.columns;
    return [
      ...pending.columns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = pending.data[tableMeta.rowIndex];
            return <OptionsMenu actions={pendingActionOptions} row={row} />;
          },
        },
      },
    ];
  }, [pending.columns, pending.data, pendingActionOptions]);

  const decidedColumns = useMemo(() => {
    if (decidedActionOptions.length === 0) return decided.columns;
    return [
      ...decided.columns,
      {
        name: 'actions', label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = decided.data[tableMeta.rowIndex];
            return <OptionsMenu actions={decidedActionOptions} row={row} />;
          },
        },
      },
    ];
  }, [decided.columns, decided.data, decidedActionOptions]);

  // AKM POC §7.1 — iki sekmede de sunucu tarafı Excel / PDF (o anki süzgeçler, TAM veri); yerleşik CSV kapalı.
  const getPendingLabels = useCallback(() => buildReportLabels(
    t, pending.columns, t('overtimeApproval.pendingTab'), statusReportLabels(pending.statusLabels),
  ), [t, pending.columns, pending.statusLabels]);
  const getDecidedLabels = useCallback(() => buildReportLabels(t, decided.columns, t('overtimeApproval.decidedTab'), {
    'decision.approved': decided.decisionLabels?.approved,
    'decision.rejected': decided.decisionLabels?.rejected,
    isStale: t('overtimeApproval.staleBadge'),
    'stale.yes': t('common.yes'),
  }), [t, decided.columns, decided.decisionLabels]);
  const pendingExport = useMemo(() => (
    <ReportExportButtons url="/v1/ta/overtime-approvals/pending/export" params={pendingQueryParams} getLabels={getPendingLabels} />
  ), [pendingQueryParams, getPendingLabels]);
  const decidedExport = useMemo(() => (
    <ReportExportButtons url="/v1/ta/overtime-approvals/export" params={decidedQueryParams} getLabels={getDecidedLabels} />
  ), [decidedQueryParams, getDecidedLabels]);

  const tabs = useMemo(() => [
    { key: 'pending', label: t('overtimeApproval.pendingTab') },
    { key: 'decided', label: t('overtimeApproval.decidedTab') },
  ], [t]);

  // useMemo çağrıları KOŞULSUZ olmalı (Rules of Hooks) — bu yüzden JSX'in kendisi burada, hangi
  // sekmenin aktif olduğuna bakılmaksızın her render'da üretilir; koşullu olan yalnız AŞAĞIDAKİ
  // {activeTab === '...' && pendingTable} render seçimidir.
  const pendingTable = useMemo(() => (
    <DataTable
      title={t('overtimeApproval.pendingTab')}
      fetching={pending.fetching}
      columns={pendingColumns}
      data={pending.data}
      totalRowCount={pending.pagination.totalRowCount}
      pageIndex={pending.pagination.pageIndex}
      pageSize={pending.pagination.pageSize}
      filterList={pendingFilterList}
      onFilterConfirm={handlePendingFilterConfirm}
      onTableChanged={handlePendingDataStateChange}
      toolbarActions={pendingExport}
      download={false}
    />
  ), [
    t, pending.fetching, pendingColumns, pending.data, pending.pagination,
    pendingFilterList, handlePendingFilterConfirm, handlePendingDataStateChange, pendingExport,
  ]);

  const decidedTable = useMemo(() => (
    <DataTable
      title={t('overtimeApproval.decidedTab')}
      fetching={decided.fetching}
      columns={decidedColumns}
      data={decided.data}
      totalRowCount={decided.pagination.totalRowCount}
      pageIndex={decided.pagination.pageIndex}
      pageSize={decided.pagination.pageSize}
      filterList={decidedFilterList}
      onFilterConfirm={handleDecidedFilterConfirm}
      onTableChanged={handleDecidedDataStateChange}
      toolbarActions={decidedExport}
      download={false}
    />
  ), [
    t, decided.fetching, decidedColumns, decided.data, decided.pagination,
    decidedFilterList, handleDecidedFilterConfirm, handleDecidedDataStateChange, decidedExport,
  ]);

  return (
    <Container>

      <HeaderRow>
        <div />
        {activeTab === 'pending' && canWrite('TA') && (
          <Button
            onClick={() => setBulkConfirmOpen(true)}
            disabled={pending.fetching || bulkRows.length === 0}
          >
            {t('overtimeApproval.bulkApprovePage')}
          </Button>
        )}
      </HeaderRow>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'pending' && bulkFailed.length > 0 && (
        <BulkFailNotice>
          <BulkFailTitle>{t('overtimeApproval.bulkFailedCount', { count: bulkFailed.length })}</BulkFailTitle>
          {bulkFailed.map((f, i) => (
            <BulkFailItem key={`${f.userId}-${f.date}-${i}`}>
              {f.userFullName || f.userId} — {f.date}: {t(`responseErrors.${f.error}`, { defaultValue: f.error })}
            </BulkFailItem>
          ))}
        </BulkFailNotice>
      )}

      {activeTab === 'pending' && pendingTable}
      {activeTab === 'decided' && decidedTable}

      <ConfirmDialog
        isOpen={bulkConfirmOpen}
        title={t('overtimeApproval.bulkApprovePage')}
        message={t('overtimeApproval.bulkConfirm', {
          count: bulkRows.length,
          minutes: formatMinutes(pendingTotalMinutes),
        })}
        onConfirm={handleBulkApproveConfirm}
        onCancel={() => setBulkConfirmOpen(false)}
        loading={bulkProcessing}
        type="info"
      />

      <GenericFormDrawer
        open={rejectDialog.isOpen}
        onClose={handleRejectCancel}
        title={t('overtimeApproval.reject')}
        schema={rejectSchema}
        initialValues={rejectInitialValues}
        onSave={handleRejectSave}
        submitLabel={t('overtimeApproval.reject')}
      />

      <GenericFormDrawer
        open={decisionDialog.isOpen}
        onClose={handleDecisionCancel}
        title={decisionDialog.mode ? t(`overtimeApproval.${decisionDialog.mode}`) : ''}
        schema={decisionSchema}
        initialValues={decisionInitialValues}
        onSave={handleDecisionSave}
        submitLabel={decisionDialog.mode ? t(`overtimeApproval.${decisionDialog.mode}`) : undefined}
      />
    </Container>
  );
}
